import {
  buildAttentionHref,
  canAccessFinancesCta,
  clinicHubHref,
} from '@/application/metrics/attentionCta';
import { canAccessClinicHub } from '@/application/navigation/menuHouses';

export type ProfessionalDetailCrossLinkId = 'clinic' | 'patientsRisk' | 'occupancy' | 'finances';

export type ProfessionalDetailCrossLink = {
  id: ProfessionalDetailCrossLinkId;
  href: string;
  /** Clave i18n bajo domain.professionals.detail.crossLinks.* */
  labelKey: string;
};

function canAccessOccupancy(hasPermission: (key: string) => boolean): boolean {
  return hasPermission('metrics.turnos') || hasPermission('reports.view');
}

function canAccessPatientsRisk(hasPermission: (key: string) => boolean): boolean {
  return hasPermission('metrics.no_volvieron');
}

/**
 * Cross-links del detalle de profesional → otros dominios (tenant + periodo).
 * Solo incluye destinos existentes y con permiso; sin botones muertos.
 */
export function professionalDetailCrossLinks(input: {
  tenantId: string;
  period: string;
  hasPermission: (key: string) => boolean;
}): ProfessionalDetailCrossLink[] {
  const { tenantId, period, hasPermission } = input;
  if (!tenantId || !period) return [];

  const periodQs = `period=${encodeURIComponent(period)}`;
  const tenantQs = `tenant=${encodeURIComponent(tenantId)}`;
  const links: ProfessionalDetailCrossLink[] = [];

  if (canAccessClinicHub(hasPermission)) {
    links.push({
      id: 'clinic',
      href: clinicHubHref(tenantId, period),
      labelKey: 'domain.professionals.detail.crossLinks.clinic',
    });
  }

  if (canAccessPatientsRisk(hasPermission)) {
    links.push({
      id: 'patientsRisk',
      href: buildAttentionHref({
        cta: 'patients',
        tenantId,
        period,
        patientsTab: 'risk',
      }),
      labelKey: 'domain.professionals.detail.crossLinks.patientsRisk',
    });
  }

  if (canAccessOccupancy(hasPermission)) {
    links.push({
      id: 'occupancy',
      href: `/reports/occupancy?${periodQs}&${tenantQs}`,
      labelKey: 'domain.professionals.detail.crossLinks.occupancy',
    });
  }

  if (canAccessFinancesCta(hasPermission)) {
    links.push({
      id: 'finances',
      href: buildAttentionHref({
        cta: 'finances',
        tenantId,
        period,
      }),
      labelKey: 'domain.professionals.detail.crossLinks.finances',
    });
  }

  return links;
}
